import { GroupMoveRequest, GroupType } from '@kcftech/prometheus-client';
import { TreeNodeMoveType } from 'components/tree/types';
import {
    SimpleTreeItem,
    Subtree,
    TreeItemInfo,
} from 'pages/workbench/side_panel/workbench_tree/useLoadSubtree';
import { TreeMoveOptions, TreeNode, TreeNodePosition } from 'shared/data_tree/types';
import { parentIsSubSystem } from 'utils/tree_item';
import { Lookup } from 'utils/types';

export const isNodeInHierarchy = <TData>(
    { id }: TreeNode<TData>,
    hierarchy: TreeNode<TData>[],
) => hierarchy.some((node) => node.id === id);

export const canAddNodeToSelections = <T>(
    node: TreeNode<T>,
    selectedNodes: TreeNode<T>[],
) =>
    selectedNodes.every(
        (selectedNode) =>
            !isNodeInHierarchy(node, selectedNode.hierarchy) &&
            !isNodeInHierarchy(selectedNode, node.hierarchy),
    );

const ValidMoveParentTypes: Lookup<GroupType, GroupType[]> = {
    [GroupType.System]: [],
    [GroupType.Group]: [GroupType.System, GroupType.Group],
    [GroupType.Assembly]: [GroupType.System, GroupType.Group],
    [GroupType.Asset]: [GroupType.System, GroupType.Group, GroupType.Assembly],
    [GroupType.MonitoringPoint]: [GroupType.Asset],
    [GroupType.IndicatorGroup]: [GroupType.MonitoringPoint],
    [GroupType.AnalyticModel]: [],
};

const GroupTypesThatCanBeMoved = Object.entries(ValidMoveParentTypes)
    .filter((pair) => pair[1].length > 0)
    .map((pair) => pair[0]);

export const canNodeBeMoved = ({ data: { treeItem } }: TreeNode<TreeItemInfo>) =>
    GroupTypesThatCanBeMoved.includes(treeItem.TreeItemType);

const canChangeParent = ({ TreeItemType }: SimpleTreeItem) =>
    TreeItemType === GroupType.Group ||
    TreeItemType === GroupType.Assembly ||
    TreeItemType === GroupType.Asset ||
    TreeItemType === GroupType.MonitoringPoint;

const isValidParent = (treeItem: SimpleTreeItem, parent: Subtree) =>
    ValidMoveParentTypes[treeItem.TreeItemType].includes(parent.TreeItemType) &&
    (treeItem.TreeItemType !== GroupType.Group || !parentIsSubSystem(parent));

export const canMoveNodeTo = (
    { data: { treeItem }, parent, prevSibling, nextSibling }: TreeNode<TreeItemInfo>,
    moveType: TreeNodePosition,
    {
        id: targetNodeId,
        data: { treeItem: targetTreeItem },
        parent: targetParent,
    }: TreeNode<TreeItemInfo>,
) => {
    // Technically this check is unnecessary, because isValidParent will handle
    // it, but this early outs. The types listed here cannot be moved at all,
    // even just reordering within the same parent is disallowed.
    if (
        treeItem.TreeItemType === GroupType.System ||
        treeItem.TreeItemType === GroupType.AnalyticModel ||
        // (checking for no parent should be unnecessary, but it makes TS happy)
        !parent
    ) {
        return false;
    }

    switch (moveType) {
        case 'before':
        case 'after': {
            // Cannot move before or after a System
            if (!targetParent) {
                return false;
            }

            const isSameParent = parent.id === targetParent.id;

            // Allow reordering
            if (isSameParent) {
                // Cannot move after previous sibling
                if (moveType === 'after' && targetNodeId === prevSibling?.id) {
                    return false;
                }
                // Cannot move before next sibling
                if (moveType === 'before' && targetNodeId === nextSibling?.id) {
                    return false;
                }
                return true;
            }
            // Validate the group is allowed to move to a new parent
            if (!canChangeParent(treeItem)) {
                return false;
            }
            // Validate the parent is a valid target
            return isValidParent(treeItem, targetParent.data.treeItem);
        }

        case 'child': {
            // Validate the group is allowed to move to a new parent
            if (!canChangeParent(treeItem)) {
                return false;
            }

            // Cannot move nowhere or to invalid parent
            const isSameParent = parent.data.treeItem.Id === targetTreeItem.Id;
            return !isSameParent && isValidParent(treeItem, targetTreeItem);
        }

        default:
            throw new Error('Invalid move type');
    }
};

export const getMoveParentId = (
    moveType: TreeNodeMoveType,
    targetNode: TreeNode<TreeItemInfo>,
) => {
    const parentId = moveType === 'child' ? targetNode.id : targetNode.parent?.id;

    if (!parentId) {
        throw new Error('Missing parent');
    }

    return parentId;
};

const MAX_INT32 = 2_147_483_647;

export const getMoveRequest = (
    moveType: TreeNodeMoveType,
    targetNode: TreeNode<TreeItemInfo>,
): GroupMoveRequest => {
    const parentId = getMoveParentId(moveType, targetNode);

    switch (moveType) {
        case 'before': {
            return new GroupMoveRequest({
                ParentId: parentId,
                SortOrder: targetNode.data.treeItem.SortOrder,
            });
        }
        case 'after': {
            return new GroupMoveRequest({
                ParentId: parentId,
                SortOrder: targetNode.data.treeItem.SortOrder + 1,
            });
        }
        case 'child':
            return new GroupMoveRequest({
                ParentId: parentId,
                SortOrder: MAX_INT32,
            });
        default:
            throw new Error('Invalid move type');
    }
};

export function getRevertMove(
    node: TreeNode<TreeItemInfo>,
): Pick<TreeMoveOptions, 'position' | 'targetId'> {
    if (node.prevSibling) {
        return { position: 'after', targetId: node.prevSibling.id };
    }
    if (node.nextSibling) {
        return { position: 'before', targetId: node.nextSibling.id };
    }
    if (node.parent) {
        return { position: 'child', targetId: node.parent.id };
    }
    throw new Error("can't revert an impossible move");
}
